import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { AuthLayout } from "@/components/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const INDUSTRIES = [
  "Casa vacanze / Hospitality", "Hotel", "Ristorazione", "Agenzia viaggi",
  "E-commerce", "Servizi professionali", "Retail", "Salute e benessere", "Altro",
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", password: "", confirm_password: "",
    company_name: "", industry: "", accept_terms: false, accept_privacy: false,
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm_password) return toast.error("Le password non coincidono");
    if (!form.accept_terms || !form.accept_privacy) return toast.error("Accetta termini e privacy");
    if (!form.industry) return toast.error("Seleziona un settore");
    setLoading(true);
    try {
      await register(form);
      toast.success("Account creato! Iniziamo la configurazione.");
      navigate("/onboarding");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Crea il tuo account" subtitle="Registra la tua azienda su Connectivity in pochi secondi">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>Nome</Label>
            <Input required value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="reg-firstname" />
          </div>
          <div className="space-y-2">
            <Label>Cognome</Label>
            <Input required value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="reg-lastname" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Email</Label>
          <Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="reg-email" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>Password</Label>
            <Input type="password" required minLength={8} value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="reg-password" />
          </div>
          <div className="space-y-2">
            <Label>Conferma</Label>
            <Input type="password" required value={form.confirm_password} onChange={(e) => set("confirm_password", e.target.value)} data-testid="reg-confirm" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Nome azienda</Label>
          <Input required value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="reg-company" />
        </div>
        <div className="space-y-2">
          <Label>Settore attività</Label>
          <Select value={form.industry} onValueChange={(v) => set("industry", v)}>
            <SelectTrigger data-testid="reg-industry"><SelectValue placeholder="Seleziona un settore" /></SelectTrigger>
            <SelectContent>
              {INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 pt-1">
          <label className="flex items-start gap-2.5 text-sm">
            <Checkbox checked={form.accept_terms} onCheckedChange={(v) => set("accept_terms", !!v)} data-testid="reg-terms" className="mt-0.5" />
            <span>Accetto i <Link to="/terms" className="text-primary hover:underline">Termini di servizio</Link></span>
          </label>
          <label className="flex items-start gap-2.5 text-sm">
            <Checkbox checked={form.accept_privacy} onCheckedChange={(v) => set("accept_privacy", !!v)} data-testid="reg-privacy" className="mt-0.5" />
            <span>Accetto l'<Link to="/privacy" className="text-primary hover:underline">Informativa privacy</Link></span>
          </label>
        </div>
        <Button type="submit" className="w-full rounded-lg" disabled={loading} data-testid="register-submit">
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Crea account
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-muted-foreground">
        Hai già un account? <Link to="/login" className="font-medium text-primary hover:underline">Accedi</Link>
      </p>
    </AuthLayout>
  );
}
